// This component deals with both the PRIIPS KIDS and UCITS KIIDs Viewers. It is used to view the KID in a PDF format.

import { FC, useEffect, useState } from 'react';
import useRouteParams from '../../../../../hooks/useRouteParams';
import { useDispatch } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import { useNavigate } from 'react-router-dom';
import Raptor2Loading from '../../../../feedback/Raptor2Loading';
import RevertVersionNumberButton from '../../components/buttons/RevertVersionNumberButton.component';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { useRaidrKids } from '../context/RaidrKidsContext';
import { useKIDsContentData } from '../services/queries';
import { useGenerateNewKIDPdf } from '../services/mutations';
import ErrorBoundaryMessage from '../../../../ui/ErrorBoundaryMessage';
import TokenService from '../../../../../services/token-service';
import { getOS } from '../../../../../utilities/helpers/common/system';
import { pdfjs } from 'react-pdf';
import KidsEditorGridItem from '../../kidsV2/layout/KidsEditorGridItem.component';
import RaptorPDFViewer from '../../../../general/raptorPDFViewer/RaptorPDFViewer';

const KidsView: FC = () => {
  const OS = getOS();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { backToOverviewLink, iframeTitle } = useRaidrKids();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  // Get route params
  const fundNameInternal = useRouteParams('fundId')['fundId'];
  const isin = useRouteParams('isin')['isin'];
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'];
  const version = useRouteParams('version')['version'];
  const latestVersion = useRouteParams('latestVersion')['latestVersion'];

  const { data, isPending, error } = useKIDsContentData(
    clientId,
    fundNameInternal,
    isin,
    documentLanguage,
    version,
  );

  const createNewKIDPdf = useGenerateNewKIDPdf();

  useEffect(() => {
    return () => {
      dispatch(toggleControlBarShowing(false));
    };
  }, [dispatch]);

  // Why I cant figure to remove this useEffect? Eventually Mr Barneveld
  useEffect(() => {
    if (!isPending && data && data.length) {
      getPDF();
      updateControlBar();
    }
  }, [data]);

  if (isPending) {
    return (
      <Raptor2Loading
        centerWrap
        messages={[`Loading Raidr ${fundNameInternal}...`]}
      />
    );
  }

  if (error) {
    return <ErrorBoundaryMessage />;
  }

  const getPDF = async () => {
    try {
      setHasError(false);
      const formData = new FormData();
      const content = data.sort((a: any, b: any) => {
        return a.kiid_index - b.kiid_index;
      });
      const priipsKidsJson = JSON.stringify(content);
      const fundNameInternal = content[0].fund_id_string;
      const fundName = content[0].fund_name;
      const isin = content[0].share_class;
      const documentLanguage = content[0].document_language;

      formData.append('contents', priipsKidsJson);
      formData.append('fund_id', fundNameInternal);
      formData.append('fund_name', fundName);
      formData.append('isin', isin);
      formData.append('document_language', documentLanguage);
      formData.append(
        'params',
        JSON.stringify([
          'contents',
          'fund_id',
          'fund_name',
          'isin',
          'document_language',
        ]),
      );

      const response = await createNewKIDPdf.mutateAsync(formData);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);

      const iframe = document.querySelector('iframe');
      if (iframe?.src) {
        iframe.src = fileUrl;
        iframe.title = iframeTitle;
      }

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      // Setting the worker for pdfjs:
      if (OS != 'linux') {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      }

      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Failed to refresh PDF:', error);
      setHasError(true);
    }
  };

  const updateControlBar = () => {
    const componentsList = [
      <BackToOverviewBtn
        backToOverview={() =>
          navigate(backToOverviewLink, {
            state: { fundId: fundNameInternal, isin },
          })
        }
        dontSetPosition
        key="back_to_overview"
      />,
      <div className="min-w-0 w-full max-h-[1.88rem] mr-[0.62rem] ml-[0.62rem] flex items-center whitespace-nowrap truncate text-gray-800">
        <span className="text-xl font-medium truncate">{`${data[0].fund_name} - ${isin}`}</span>
      </div>,
      <div className="inline-flex items-center h-[1.88rem] max-h-[1.88rem] mr-[0.62rem] ml-[0.62rem] whitespace-nowrap bg-[#274C77] px-2 py-0.5 text-white text-[0.75rem] font-semibold shadow-sm leading-none rounded-[3px]">
        <span className="text-lg font-medium leading-none">{`Version: ${version}`}</span>
      </div>,
    ];
    if (version !== latestVersion) {
      componentsList.push(
        <RevertVersionNumberButton
          fundId={data[0].fund_id || ''}
          shareClass={isin || ''}
          version={version || ''}
          documentLanguage={documentLanguage || ''}
          backToOverviewLink={backToOverviewLink}
        />,
      );
    }
    dispatch(updateControlBarComponents(componentsList));
    dispatch(toggleControlBarShowing(true));
  };

  return (
    <KidsEditorGridItem xs={12}>
      <RaptorPDFViewer
        pdfUrl={pdfUrl}
        fileName={iframeTitle}
        hasError={hasError}
        errorMessage={''}
        setHasError={setHasError}
        isPending={createNewKIDPdf.isPending}
      />
    </KidsEditorGridItem>
  );
};

export default KidsView;
